import React, { useEffect, useState } from "react";
import {
    Form,
    Input,
    Button,
    DatePicker,
    Select,
    InputNumber,
    Upload,
    message,
    Card,
    Row,
    Col,
    Divider,

    Typography,
    Modal,
} from "antd";
import { UploadOutlined, SaveOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import { useHistory, useParams } from "react-router-dom";
import axiosInstance from "../../../configs";
import dayjs from "dayjs";

const { Option } = Select;
const { TextArea } = Input;
const { Title, Text } = Typography;

const PaymentReceivedForm = () => {
    const history = useHistory();
    const { id } = useParams();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [fileList1, setFileList1] = useState([]);
    const [fileList2, setFileList2] = useState([]);
    const [fileList3, setFileList3] = useState([]);
    const [contacts, setContacts] = useState([]);
    const [invoices, setInvoices] = useState([]);
    const [selectedInvoice, setSelectedInvoice] = useState(null);

    // Confirmation Modal State
    const [confirmVisible, setConfirmVisible] = useState(false);
    const [pendingValues, setPendingValues] = useState(null);

    useEffect(() => {
        fetchContacts();
        fetchInvoices();
        if (id) {
            fetchData(id);
        } else {
            generatePaymentNumber();
        }
    }, [id]);

    const fetchContacts = async () => {
        try {
            const response = await axiosInstance.get("/finance/contacts");
            setContacts(response.data.data || []);
        } catch (error) {
            console.error("Error fetching contacts:", error);
        }
    };

    const fetchInvoices = async () => {
        try {
            // Only fetch UNPAID invoices for new payments
            const response = await axiosInstance.get("/finance/sales-invoices", {
                params: { status: 'UNPAID', size: 100 }
            });
            setInvoices(response.data.data.content || []);
        } catch (error) {
            console.error("Error fetching invoices:", error);
        }
    };

    const handleInvoiceChange = (invoiceId) => {
        const inv = invoices.find(i => i.id_invoice === invoiceId);
        setSelectedInvoice(inv);
        if (inv) {
            form.setFieldsValue({
                amount: inv.total_amount_net || inv.amount,
                idContact: inv.id_customer // Auto select customer if possible
            });
        }
    };

    const generatePaymentNumber = async () => {
        try {
            const response = await axiosInstance.get("/finance/payment-received/generate-number");
            form.setFieldsValue({ paymentNumber: response.data.paymentNumber });
        } catch (error) {
            console.error("Error generating payment number:", error);
        }
    };

    const fetchData = async (id) => {
        setLoading(true);
        try {
            const response = await axiosInstance.get(`/finance/payment-received/${id}`);
            const data = response.data.data;
            form.setFieldsValue({
                ...data,
                paymentDate: data.paymentDate ? dayjs(data.paymentDate) : null,
            });
            // Handle file lists if needed
        } catch (error) {
            console.error(error);
            message.error("Gagal mengambil data.");
        } finally {
            setLoading(false);
        }
    };

    const onFinish = async (values) => {
        setPendingValues(values);
        setConfirmVisible(true);
    };

    const handleConfirmSubmit = async () => {
        const values = pendingValues;
        setLoading(true);
        const formData = new FormData();
        Object.keys(values).forEach((key) => {
            if (values[key] !== undefined && values[key] !== null) {
                if (dayjs.isDayjs(values[key])) {
                    formData.append(key, values[key].format("YYYY-MM-DD"));
                } else {
                    formData.append(key, values[key]);
                }
            }
        });

        if (fileList1.length > 0) formData.append("photo1", fileList1[0].originFileObj);
        if (fileList2.length > 0) formData.append("photo2", fileList2[0].originFileObj);
        if (fileList3.length > 0) formData.append("photo3", fileList3[0].originFileObj);

        try {
            if (id) {
                await axiosInstance.put(`/finance/payment-received/${id}`, formData, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                message.success("Data berhasil diperbarui");
            } else {
                await axiosInstance.post("/finance/payment-received", formData, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                message.success("Data berhasil disimpan");
            }
            history.push("/finance/payment-received");
        } catch (error) {
            console.error(error);
            message.error("Gagal menyimpan data: " + (error.response?.data?.error || error.message));
        } finally {
            setLoading(false);
            setConfirmVisible(false);
        }
    };

    const uploadProps = (setFileList) => ({
        onRemove: (file) => {
            setFileList((prev) => {
                const index = prev.indexOf(file);
                const newFileList = prev.slice();
                newFileList.splice(index, 1);
                return newFileList;
            });
        },
        beforeUpload: (file) => {
            setFileList([file]);
            return false;
        },
        fileList: setFileList,
    });

    return (
        <Card className="kt-portlet" bodyStyle={{ padding: 24 }}>
            <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
                <Col>
                    <Title level={3}>
                        {id ? "Edit Pembayaran" : "Buat Pembayaran Baru"}
                    </Title>
                </Col>
                <Col>
                    <Button icon={<ArrowLeftOutlined />} onClick={() => history.push("/finance/payment-received")}>
                        Kembali
                    </Button>
                </Col>
            </Row>

            <Form form={form} layout="vertical" onFinish={onFinish}>
                <Row gutter={24}>
                    <Col xs={24} md={12}>
                        <Form.Item
                            name="idContact"
                            label="Pembeli"
                            rules={[{ required: true, message: "Harap pilih pembeli" }]}
                        >
                            <Select placeholder="Pilih Pembeli" showSearch optionFilterProp="children">
                                {contacts.map((contact) => (
                                    <Option key={contact.idContact} value={contact.idContact}>
                                        {contact.name}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item
                            name="idInvoice"
                            label="No. Invoice"
                            rules={[{ required: true, message: "Harap pilih invoice" }]}
                        >
                            <Select
                                placeholder="Pilih No. Invoice"
                                showSearch
                                optionFilterProp="children"
                                onChange={handleInvoiceChange}
                            >
                                {invoices.map((inv) => (
                                    <Option key={inv.id_invoice} value={inv.id_invoice}>
                                        {inv.invoice_number}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={24}>
                    <Col xs={24} md={8}>
                        <Form.Item label="Tagihan">
                            <Input
                                disabled
                                value={selectedInvoice ? `Rp ${(selectedInvoice.total_amount_net || selectedInvoice.amount).toLocaleString()}` : 'IDR'}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                        <Form.Item label="Pembayaran" name="amount" rules={[{ required: true }]}>
                            <InputNumber
                                style={{ width: "100%" }}
                                formatter={(value) => `Rp ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                                parser={(value) => value.replace(/\Rp\s?|(,*)/g, "")}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                        <Form.Item
                            label="Sisa Tagihan"
                            shouldUpdate={(prevValues, curValues) => prevValues.amount !== curValues.amount || prevValues.idInvoice !== curValues.idInvoice}
                        >
                            {() => {
                                const val = form.getFieldValue('amount') || 0;
                                const total = selectedInvoice ? (selectedInvoice.total_amount_net || selectedInvoice.amount) : 0;
                                return (
                                    <Input
                                        disabled
                                        value={selectedInvoice ? `Rp ${(total - val).toLocaleString()}` : 'IDR'}
                                    />
                                );
                            }}
                        </Form.Item>
                    </Col>
                </Row>

                <Divider />

                <Row gutter={24}>
                    <Col xs={24} md={12}>
                        <Form.Item
                            name="paymentNumber"
                            label="No. Pembayaran"
                            rules={[{ required: true }]}
                        >
                            <Input disabled />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item
                            name="paymentDate"
                            label="Tgl. Pembayaran"
                            rules={[{ required: true }]}
                        >
                            <DatePicker style={{ width: "100%" }} format="DD-MM-YYYY" />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={24}>
                    <Col xs={24} md={12}>
                        <Form.Item
                            name="paymentMethod"
                            label="Metode Pembayaran"
                            rules={[{ required: true }]}
                        >
                            <Select placeholder="Pilih Metode Pembayaran">
                                <Option value="Transfer">Transfer</Option>
                                <Option value="Cash">Cash</Option>
                                <Option value="Cek">Cek</Option>
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item
                            name="destinationAccount"
                            label="Tujuan Pembayaran"
                            rules={[{ required: true }]}
                        >
                            <Input placeholder="Pilih Tujuan Pembayaran" />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={24}>
                    <Col xs={24} md={12}>
                        <Form.Item name="referenceNumber" label="No. Referensi">
                            <Input placeholder="No. Referensi" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item name="notes" label="Catatan">
                            <TextArea rows={4} placeholder="Catatan" />
                        </Form.Item>
                    </Col>
                </Row>

                <Divider />

                <Row gutter={24}>
                    <Col xs={24} sm={8} md={8}>
                        <Form.Item label="Foto 1">
                            <Upload {...uploadProps(setFileList1)} fileList={fileList1} listType="picture">
                                <Button icon={<UploadOutlined />}>Pilih File</Button>
                            </Upload>
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={8} md={8}>
                        <Form.Item label="Foto 2">
                            <Upload {...uploadProps(setFileList2)} fileList={fileList2} listType="picture">
                                <Button icon={<UploadOutlined />}>Pilih File</Button>
                            </Upload>
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={8} md={8}>
                        <Form.Item label="Foto 3">
                            <Upload {...uploadProps(setFileList3)} fileList={fileList3} listType="picture">
                                <Button icon={<UploadOutlined />}>Pilih File</Button>
                            </Upload>
                        </Form.Item>
                    </Col>
                </Row>

                <Form.Item>
                    <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={loading} block>
                        SIMPAN
                    </Button>
                </Form.Item>

            </Form>

            {/* Confirmation Modal */}
            <Modal
                title="Konfirmasi Pembayaran"
                open={confirmVisible}
                onOk={handleConfirmSubmit}
                onCancel={() => setConfirmVisible(false)}
                okText="Ya, Simpan"
                cancelText="Batal"
                confirmLoading={loading}
                width={600}
            >
                {pendingValues && (
                    <div>
                        <p>Apakah Anda yakin ingin menyimpan data pembayaran ini?</p>
                        <div style={{ background: "#f5f5f5", padding: 10, borderRadius: 4, marginBottom: 16 }}>
                            <p style={{ margin: 0 }}>
                                <strong>Pembeli:</strong> {contacts.find(c => c.idContact === pendingValues.idContact)?.name || pendingValues.idContact}
                            </p>
                            <p style={{ margin: 0 }}>
                                <strong>No Invoice:</strong> {invoices.find(i => i.id_invoice === pendingValues.idInvoice)?.invoice_number || pendingValues.idInvoice}
                            </p>
                            <p style={{ margin: 0 }}>
                                <strong>Jumlah:</strong> Rp {Number(pendingValues.amount).toLocaleString()}
                            </p>
                        </div>
                    </div>
                )}
            </Modal>
        </Card >
    );
};

export default PaymentReceivedForm;
